import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  TrendingUp,
  AlertTriangle,
  Gauge,
  Image,
  ArrowUp,
  ArrowDown,
  Brain,
  TrendingDown,
  Lightbulb,
  Link2,
  Zap,
  Calendar,
  Globe,
  Network,
  UserCheck,
  DollarSign,
  Clock,
  Shield,
  Wifi,
  Check,
  X,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Square,
  CheckSquare,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/client";
import ScoreRing from "./ScoreRing";
import KeywordSparkline from "./KeywordSparkline";
import KeywordDetailModal from "./KeywordDetailModal";

function StatCard({ icon: Icon, label, value, color = "text-copper", to }) {
  const content = (
    <div className={`bg-dark-300 border border-dark-50 rounded-xl p-5 ${to ? "hover:border-copper/30 cursor-pointer transition-colors" : ""}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-dark-50/50 rounded-lg">
          <Icon size={18} className={color} />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <div className="text-2xl font-mono font-bold">{value ?? "—"}</div>
    </div>
  );
  if (to) return <Link to={to}>{content}</Link>;
  return content;
}

function TrendBadge({ trend, change }) {
  if (trend === "rising")
    return (
      <span className="flex items-center gap-1 text-xs text-green-400 font-mono">
        <ArrowUp size={12} />
        {change > 0 ? `+${change.toFixed(1)}` : ""}
      </span>
    );
  if (trend === "falling")
    return (
      <span className="flex items-center gap-1 text-xs text-red-400 font-mono">
        <ArrowDown size={12} />
        {change < 0 ? change.toFixed(1) : ""}
      </span>
    );
  return <span className="text-xs text-gray-500 font-mono">—</span>;
}

/* Toast notification */
function Toast({ message, type = "success", onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 ${
      type === "success" ? "bg-green-900/90 text-green-200 border border-green-700" :
      type === "error" ? "bg-red-900/90 text-red-200 border border-red-700" :
      "bg-blue-900/90 text-blue-200 border border-blue-700"
    }`}>
      {type === "success" && <Check size={14} />}
      {type === "error" && <X size={14} />}
      {type === "info" && <RefreshCw size={14} />}
      {message}
    </div>
  );
}

/* Confirmation modal for bulk actions */
function ConfirmModal({ title, message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-base font-semibold text-gray-200 mb-2">{title}</h3>
        <p className="text-sm text-gray-400 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm bg-copper/20 text-copper border border-copper/30 rounded-lg hover:bg-copper/30 transition-colors">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

/* Critical Issues Panel with actions */
function CriticalIssuesPanel({ selectedSite, legacyIssues = [], crawlIssues = [] }) {
  const [filter, setFilter] = useState("open");
  const [selected, setSelected] = useState(new Set());
  const [issues, setIssues] = useState([]);
  const [toast, setToast] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [resolvedIssues, setResolvedIssues] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    // Combine legacy and crawl issues
    const combined = [
      ...legacyIssues.map(i => ({ ...i, source: "legacy" })),
      ...crawlIssues.map(i => ({ ...i, source: "crawl" })),
    ];
    setIssues(combined.filter(i => !["marked_fixed", "dismissed", "auto_resolved", "fixed"].includes(i.fix_status)));
    setResolvedIssues(combined.filter(i => ["marked_fixed", "dismissed", "auto_resolved", "fixed"].includes(i.fix_status)));
    setSelected(new Set());
  }, [legacyIssues, crawlIssues]);

  const websiteId = selectedSite?.id;

  const handleAction = useCallback(async (issueId, action) => {
    if (!websiteId) return;
    setActionLoading(prev => ({ ...prev, [issueId]: action }));
    try {
      const endpoint = `/websites/${websiteId}/crawl/issues/${issueId}/${action}/`;
      await api.post(endpoint, {});
      setIssues(prev => prev.filter(i => i.id !== issueId || i.source === "legacy"));
      if (action === "recheck") {
        setToast({ message: "Re-check complete", type: "info" });
      } else {
        setToast({ message: action === "mark-fixed" ? "Issue marked as fixed" : "Issue dismissed", type: "success" });
      }
      // Reload crawl issues
      try {
        const res = await api.get(`/websites/${websiteId}/crawl/issues/?status=open&severity=critical`);
        const resolved = await api.get(`/websites/${websiteId}/crawl/issues/?status=resolved&severity=critical`);
        setIssues([
          ...legacyIssues.map(i => ({ ...i, source: "legacy" })),
          ...res.data.map(i => ({ ...i, source: "crawl" })),
        ].filter(i => !["marked_fixed", "dismissed", "auto_resolved", "fixed"].includes(i.fix_status)));
        setResolvedIssues(resolved.data.map(i => ({ ...i, source: "crawl" })));
      } catch (e) { /* ignore reload errors */ }
    } catch (err) {
      setToast({ message: `Action failed: ${err.message}`, type: "error" });
    }
    setActionLoading(prev => ({ ...prev, [issueId]: null }));
  }, [websiteId, legacyIssues]);

  const handleBulkAction = useCallback(async (action) => {
    if (!websiteId || selected.size === 0) return;
    const crawlIds = [...selected].filter(id => {
      const issue = issues.find(i => i.id === id);
      return issue && issue.source === "crawl";
    });
    if (crawlIds.length === 0) return;

    try {
      await api.post(`/websites/${websiteId}/crawl/issues/bulk-action/`, {
        ids: crawlIds,
        action: action,
      });
      setSelected(new Set());
      setToast({
        message: `${crawlIds.length} issue(s) ${action === "fix" ? "marked as fixed" : action === "dismiss" ? "dismissed" : "queued for re-check"}`,
        type: "success",
      });
      // Reload
      const res = await api.get(`/websites/${websiteId}/crawl/issues/?status=open&severity=critical`);
      const resolved = await api.get(`/websites/${websiteId}/crawl/issues/?status=resolved&severity=critical`);
      setIssues([
        ...legacyIssues.map(i => ({ ...i, source: "legacy" })),
        ...res.data.map(i => ({ ...i, source: "crawl" })),
      ].filter(i => !["marked_fixed", "dismissed", "auto_resolved", "fixed"].includes(i.fix_status)));
      setResolvedIssues(resolved.data.map(i => ({ ...i, source: "crawl" })));
    } catch (err) {
      setToast({ message: `Bulk action failed: ${err.message}`, type: "error" });
    }
    setConfirm(null);
  }, [websiteId, selected, issues, legacyIssues]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === issues.length) setSelected(new Set());
    else setSelected(new Set(issues.map(i => i.id)));
  };

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-300">Critical Issues</h3>
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{selected.size} selected</span>
            <button
              onClick={() => handleBulkAction("fix")}
              className="px-2 py-1 text-[10px] bg-green-900/30 text-green-400 rounded hover:bg-green-900/50 transition-colors"
            >
              Fix
            </button>
            <button
              onClick={() => setConfirm({ action: "dismiss" })}
              className="px-2 py-1 text-[10px] bg-gray-700/50 text-gray-400 rounded hover:bg-gray-700/70 transition-colors"
            >
              Dismiss
            </button>
            <button
              onClick={() => handleBulkAction("recheck")}
              className="px-2 py-1 text-[10px] bg-blue-900/30 text-blue-400 rounded hover:bg-blue-900/50 transition-colors"
            >
              Re-check
            </button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {issues.map((issue) => (
          <div key={`${issue.source}-${issue.id}`} className="flex items-start gap-2 text-sm group">
            {issue.source === "crawl" && (
              <button
                onClick={() => toggleSelect(issue.id)}
                className="mt-0.5 flex-shrink-0 text-gray-600 hover:text-gray-400"
              >
                {selected.has(issue.id)
                  ? <CheckSquare size={14} className="text-copper" />
                  : <Square size={14} />
                }
              </button>
            )}
            <AlertTriangle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-gray-200">{issue.description}</div>
              <div className="text-xs text-gray-500 truncate mt-0.5">{issue.page_url}</div>
            </div>
            {issue.source === "crawl" && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <button
                  onClick={() => handleAction(issue.id, "mark-fixed")}
                  disabled={actionLoading[issue.id]}
                  className="p-1 rounded hover:bg-green-900/30 text-gray-500 hover:text-green-400 transition-colors"
                  title="Mark as Fixed"
                >
                  {actionLoading[issue.id] === "mark-fixed" ? <RefreshCw size={12} className="animate-spin" /> : <Check size={12} />}
                </button>
                <button
                  onClick={() => handleAction(issue.id, "dismiss")}
                  disabled={actionLoading[issue.id]}
                  className="p-1 rounded hover:bg-gray-700/50 text-gray-500 hover:text-gray-300 transition-colors"
                  title="Dismiss"
                >
                  {actionLoading[issue.id] === "dismiss" ? <RefreshCw size={12} className="animate-spin" /> : <X size={12} />}
                </button>
                <button
                  onClick={() => handleAction(issue.id, "recheck")}
                  disabled={actionLoading[issue.id]}
                  className="p-1 rounded hover:bg-blue-900/30 text-gray-500 hover:text-blue-400 transition-colors"
                  title="Re-check"
                >
                  {actionLoading[issue.id] === "recheck" ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                </button>
              </div>
            )}
          </div>
        ))}
        {issues.length === 0 && (
          <div className="text-gray-500 text-sm">No critical issues</div>
        )}
      </div>

      {/* Recently Resolved (collapsed) */}
      {resolvedIssues.length > 0 && (
        <div className="mt-4 pt-3 border-t border-dark-50">
          <button
            onClick={() => setShowResolved(!showResolved)}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-400 transition-colors w-full"
          >
            {showResolved ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            Recently Resolved ({resolvedIssues.length})
          </button>
          {showResolved && (
            <div className="mt-2 space-y-2">
              {resolvedIssues.map((issue) => (
                <div key={`resolved-${issue.id}`} className="flex items-start gap-3 text-sm opacity-60">
                  <Check size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-400 line-through">{issue.description}</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {issue.fix_status === "auto_resolved" ? "Auto-resolved" :
                       issue.fix_status === "marked_fixed" ? "Marked as fixed" :
                       issue.fix_status === "dismissed" ? "Dismissed" : "Fixed"}
                      {issue.resolved_at && ` — ${new Date(issue.resolved_at).toLocaleDateString()}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      {confirm && (
        <ConfirmModal
          title="Dismiss selected issues?"
          message={`This will dismiss ${selected.size} issue(s). They can be viewed in the Resolved section.`}
          onConfirm={() => handleBulkAction("dismiss")}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

export default function Dashboard({ selectedSite }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [pagePerf, setPagePerf] = useState([]);
  const [detailKeyword, setDetailKeyword] = useState(null);
  const [topicClusters, setTopicClusters] = useState([]);
  const [leadsWidget, setLeadsWidget] = useState(null);
  const [wpHealth, setWpHealth] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = selectedSite ? { website: selectedSite.id } : {};
    api
      .get("/dashboard/", { params })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selectedSite]);

  useEffect(() => {
    if (!selectedSite) return;
    api.get(`/websites/${selectedSite.id}/ai-summary/`)
      .then((res) => setAiSummary(res.data))
      .catch(() => setAiSummary(null));
    api.get(`/websites/${selectedSite.id}/pages/upcoming/`)
      .then((res) => setUpcoming(res.data || []))
      .catch(() => setUpcoming([]));
    api.get(`/websites/${selectedSite.id}/pages/performance/`)
      .then((res) => setPagePerf(res.data || []))
      .catch(() => setPagePerf([]));
    api.get(`/websites/${selectedSite.id}/topic-clusters/`)
      .then((res) => setTopicClusters(res.data.results || res.data || []))
      .catch(() => setTopicClusters([]));
    api.get(`/websites/${selectedSite.id}/leads/widget/`)
      .then((res) => setLeadsWidget(res.data))
      .catch(() => setLeadsWidget(null));
    api.get(`/websites/${selectedSite.id}/bridge/health/`)
      .then((res) => setWpHealth(res.data))
      .catch(() => setWpHealth(null));
  }, [selectedSite]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-dark-300 rounded-xl" />
          ))}
        </div>
        <div className="h-96 bg-dark-300 rounded-xl" />
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Dashboard</h2>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          icon={Search}
          label="Total Keywords"
          value={stats.total_keywords?.toLocaleString()}
          to="/keywords"
        />
        <StatCard
          icon={TrendingUp}
          label="Avg Position"
          value={stats.avg_position}
          color="text-green-400"
          to="/keywords"
        />
        <StatCard
          icon={AlertTriangle}
          label="Total Issues"
          value={stats.total_issues}
          color="text-amber-400"
          to="/audit"
        />
        <StatCard
          icon={Gauge}
          label="CWV Score"
          value={stats.cwv_score}
          color="text-blue-400"
          to="/vitals"
        />
        <StatCard
          icon={Image}
          label="Missing Alt Text"
          value={stats.images_missing_alt}
          color="text-purple-400"
          to="/alt-text"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Rising Keywords */}
        <div className="lg:col-span-2 bg-dark-300 border border-dark-50 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-300 mb-1">
            Top Rising Keywords
          </h3>
          <p className="text-[10px] text-gray-500 mb-4">Source: GSC 28d (position improvement)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-dark-50">
                  <th className="text-left py-2 font-medium">Keyword</th>
                  <th className="text-right py-2 font-medium">Position</th>
                  <th className="text-right py-2 font-medium">Change</th>
                  <th className="text-right py-2 font-medium">Impr.</th>
                  <th className="text-right py-2 font-medium">Clicks</th>
                  <th className="py-2 font-medium">Trend</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rising_keywords || []).map((kw) => (
                  <tr
                    key={kw.id}
                    className="border-b border-dark-50/50 hover:bg-dark-50/30 cursor-pointer"
                    onClick={() => setDetailKeyword(kw.keyword)}
                  >
                    <td className="py-2.5 font-mono text-gray-200 hover:text-copper transition-colors">
                      {kw.keyword}
                    </td>
                    <td className="py-2.5 text-right font-mono">
                      {kw.current_position?.toFixed(1)}
                    </td>
                    <td className="py-2.5 text-right">
                      <TrendBadge
                        trend={kw.trend}
                        change={kw.position_change}
                      />
                    </td>
                    <td className="py-2.5 text-right font-mono text-gray-400">
                      {kw.impressions}
                    </td>
                    <td className="py-2.5 text-right font-mono text-gray-400">
                      {kw.clicks}
                    </td>
                    <td className="py-2.5 flex justify-center">
                      <KeywordSparkline data={kw.history || []} />
                    </td>
                  </tr>
                ))}
                {(!data?.rising_keywords || !data.rising_keywords.length) && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      No rising keywords yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Critical Issues — now with actions */}
          <CriticalIssuesPanel
            selectedSite={selectedSite}
            legacyIssues={data?.critical_issues || []}
            crawlIssues={data?.crawl_critical_issues || []}
          />

          {/* CWV Summary */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">
              Core Web Vitals
            </h3>
            <div className="flex justify-around">
              <ScoreRing
                score={stats.cwv_score || 0}
                label="Overall"
                size={72}
              />
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights Summary */}
      {aiSummary && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <Brain size={16} className="text-copper" /> AI Insights
            </h3>
            <Link to="/ai-insights" className="text-xs text-copper hover:underline">View all</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-900/20 rounded-lg">
                <TrendingDown size={16} className="text-red-400" />
              </div>
              <div>
                <div className="text-lg font-mono font-bold">{aiSummary.decaying_pages || 0}</div>
                <div className="text-xs text-gray-500">Decaying Pages</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-900/20 rounded-lg">
                <Lightbulb size={16} className="text-blue-400" />
              </div>
              <div>
                <div className="text-lg font-mono font-bold">{aiSummary.keyword_opportunities || 0}</div>
                <div className="text-xs text-gray-500">Keyword Opps</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-900/20 rounded-lg">
                <Link2 size={16} className="text-amber-400" />
              </div>
              <div>
                <div className="text-lg font-mono font-bold">{aiSummary.link_suggestions || 0}</div>
                <div className="text-xs text-gray-500">Link Suggestions</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-copper/20 rounded-lg">
                <Zap size={16} className="text-copper" />
              </div>
              <div>
                <div className="text-lg font-mono font-bold">
                  {!selectedSite?.wp_connected ? "No WP" : selectedSite?.autopilot_enabled ? (selectedSite.autopilot_paused ? "Paused" : "Active") : "Off"}
                </div>
                <div className="text-xs text-gray-500">Auto-Pilot</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WordPress Health Widget */}
      {wpHealth && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <Wifi size={16} className="text-green-400" /> WordPress Plugin
            </h3>
            <Link to="/wordpress-sync" className="text-xs text-copper hover:underline">Manage</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <div className="text-lg font-mono font-bold text-green-400">v{wpHealth.plugin_version || wpHealth.version || "?"}</div>
              <div className="text-xs text-gray-500">Plugin</div>
            </div>
            <div>
              <div className="text-lg font-mono font-bold">{wpHealth["404_stats"]?.unresolved ?? wpHealth.four_oh_four_stats?.unresolved ?? "--"}</div>
              <div className="text-xs text-gray-500">404s</div>
            </div>
            <div>
              <div className="text-lg font-mono font-bold">{wpHealth.redirect_count ?? "--"}</div>
              <div className="text-xs text-gray-500">Redirects</div>
            </div>
            <div>
              <div className="text-lg font-mono font-bold">{wpHealth.insights_summary?.avg_score ?? "--"}</div>
              <div className="text-xs text-gray-500">SEO Score</div>
            </div>
            <div>
              <div className="text-lg font-mono font-bold">{wpHealth.insights_summary?.pages_analyzed ?? "--"}</div>
              <div className="text-xs text-gray-500">Pages Analyzed</div>
            </div>
          </div>
        </div>
      )}

      {/* Topic Clusters Widget */}
      {topicClusters.length > 0 && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <Network size={16} className="text-copper" /> Topic Clusters
            </h3>
            <Link to="/topic-clusters" className="text-xs text-copper hover:underline">View all</Link>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div>
              <div className="text-lg font-mono font-bold text-copper">{topicClusters.length}</div>
              <div className="text-xs text-gray-500">Active Clusters</div>
            </div>
            <div>
              <div className="text-lg font-mono font-bold">
                {Math.round(topicClusters.reduce((s, c) => s + (c.cluster_score || 0), 0) / topicClusters.length)}%
              </div>
              <div className="text-xs text-gray-500">Avg Completion</div>
            </div>
            <div>
              <div className="text-lg font-mono font-bold">
                {topicClusters.reduce((s, c) => s + (c.total_cluster_pages || 0), 0)}
              </div>
              <div className="text-xs text-gray-500">Total Pages</div>
            </div>
          </div>
          <div className="space-y-2">
            {topicClusters.slice(0, 3).map((c) => (
              <Link key={c.id} to="/topic-clusters" className="flex items-center justify-between text-sm hover:bg-dark-50/30 rounded-lg px-2 py-1.5 -mx-2 transition-colors">
                <span className="text-gray-200 truncate max-w-[250px]">{c.name}</span>
                <span className="text-xs font-mono text-gray-500">
                  <span className="text-green-400">{c.published_cluster_pages || 0}</span>
                  /{c.total_cluster_pages || 0} pages, score {c.cluster_score || 0}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Leads Widget */}
      {leadsWidget && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <UserCheck size={16} className="text-copper" /> Leads
            </h3>
            <Link to="/leads" className="text-xs text-copper hover:underline">View all</Link>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-lg font-mono font-bold text-copper">
                {leadsWidget.leads_this_week}
                {leadsWidget.week_change !== 0 && (
                  <span className={`text-xs ml-1 ${leadsWidget.week_change > 0 ? "text-green-400" : "text-red-400"}`}>
                    {leadsWidget.week_change > 0 ? "+" : ""}{leadsWidget.week_change}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500">This Week</div>
            </div>
            <div>
              <div className="text-lg font-mono font-bold">
                ${Number(leadsWidget.pipeline_value || 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Pipeline</div>
            </div>
            <div>
              <div className={`text-lg font-mono font-bold ${leadsWidget.overdue_followups > 0 ? "text-red-400" : ""}`}>
                {leadsWidget.overdue_followups}
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-1">
                {leadsWidget.overdue_followups > 0 && <Clock size={10} className="text-red-400" />}
                Follow-ups Due
              </div>
            </div>
          </div>
          {(leadsWidget.latest_leads || []).length > 0 && (
            <div className="space-y-2">
              {leadsWidget.latest_leads.map((l) => (
                <Link key={l.id} to="/leads" className="flex items-center justify-between text-sm hover:bg-dark-50/30 rounded-lg px-2 py-1.5 -mx-2 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-gray-200 truncate">{l.name || l.email || l.phone}</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${
                      l.source === "organic" ? "bg-green-900/30 text-green-400" :
                      l.source === "gbp" ? "bg-amber-900/30 text-amber-400" :
                      "bg-gray-700/50 text-gray-400"
                    }`}>{l.source}</span>
                  </div>
                  <span className="text-xs text-gray-600 flex-shrink-0 ml-2">
                    {new Date(l.created_at).toLocaleDateString()}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bottom row: Upcoming Publications + New Page Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Publications */}
        {upcoming.length > 0 && (
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <Calendar size={16} className="text-copper" /> Upcoming Publications
              </h3>
              <Link to="/calendar" className="text-xs text-copper hover:underline">View calendar</Link>
            </div>
            <div className="space-y-2">
              {upcoming.map((p) => {
                const schedDate = p.schedule_date ? new Date(p.schedule_date) : null;
                const diff = schedDate ? Math.ceil((schedDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                return (
                  <div key={p.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-200 truncate max-w-[250px]">{p.title}</span>
                    <span className="text-xs font-mono text-gray-500">
                      {diff != null ? (diff <= 0 ? "Today" : `in ${diff}d`) : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* New Page Performance */}
        {pagePerf.length > 0 && (
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <Globe size={16} className="text-copper" /> New Page Performance
              </h3>
              <Link to="/pages" className="text-xs text-copper hover:underline">View all</Link>
            </div>
            <div className="space-y-2">
              {pagePerf.slice(0, 5).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <div className="truncate max-w-[200px] text-gray-200">{p.title}</div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className={`px-1.5 py-0.5 rounded ${
                      p.indexing_status === "indexed" ? "bg-green-900/20 text-green-400" :
                      p.indexing_status === "submitted" ? "bg-blue-900/20 text-blue-400" :
                      p.indexing_status === "failed" ? "bg-red-900/20 text-red-400" :
                      "bg-gray-800 text-gray-500"
                    }`}>
                      {p.indexing_status || "pending"}
                    </span>
                    {p.current_position && (
                      <span className="font-mono text-copper">#{Math.round(p.current_position)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {detailKeyword && (
        <KeywordDetailModal
          keyword={detailKeyword}
          selectedSite={selectedSite}
          onClose={() => setDetailKeyword(null)}
        />
      )}
    </div>
  );
}
